import { useState } from "preact/hooks";
import { apiClient, dashboardInjectContextEndpoint } from "../../constants.ts";
import { Button, inputBaseClass } from "./components.tsx";
import { useAuth } from "./db.ts";

export type PreferredNetwork =
  | "whatsapp"
  | "whatsapp-demo"
  | "whatsapp-demo-business-integration"
  | "whatsapp-for-business"
  | "telegram"
  | "slack"
  | "teams"
  | "alice-and-bot"
  | "email"
  | "facebook-messenger";

export const InjectContextByUserId = (
  { botId, initialNetwork, initialConversationId }: {
    botId: string;
    initialNetwork?: PreferredNetwork | "";
    initialConversationId?: string;
  },
) => {
  const auth = useAuth();
  const [network, setNetwork] = useState<PreferredNetwork | "">(
    initialNetwork ?? "",
  );
  const [conversationId, setConversationId] = useState(
    initialConversationId ?? "",
  );
  const [context, setContext] = useState("");
  const [subject, setSubject] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!network || !conversationId || !context) return;
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await apiClient({
        endpoint: dashboardInjectContextEndpoint,
        token: auth.user?.refresh_token ?? "",
        payload: {
          botId,
          network,
          subject: network === "email" ? subject : undefined,
          conversationId,
          context,
        },
      });

      if (!response.success) {
        setError(response.error ?? "Failed to inject context");
      } else {
        setSuccess(true);
        setContext("");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary-light dark:text-text-primary">
          Network
        </label>
        <select
          value={network}
          onChange={(e) =>
            setNetwork(
              (e.target as
                | HTMLInputElement
                | HTMLTextAreaElement
                | HTMLSelectElement).value as PreferredNetwork | "",
            )}
          className={`w-full ${inputBaseClass}`}
          required
        >
          <option value="" disabled>Select a network</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="whatsapp-demo">WhatsApp Demo</option>
          <option value="whatsapp-demo-business-integration">
            WhatsApp Official Demo
          </option>
          <option value="whatsapp-for-business">WhatsApp for Business</option>
          <option value="telegram">Telegram</option>
          <option value="slack">Slack</option>
          <option value="teams">Microsoft Teams</option>
          <option value="email">Email</option>
          <option value="alice-and-bot">Alice and Bot</option>
          <option value="facebook-messenger">Facebook Messenger</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary-light dark:text-text-primary">
          Conversation ID
        </label>
        <input
          type="text"
          value={conversationId}
          onChange={(e) =>
            setConversationId(
              (e.target as
                | HTMLInputElement
                | HTMLTextAreaElement
                | HTMLSelectElement).value,
            )}
          placeholder={network === "whatsapp" ||
              network === "whatsapp-demo" ||
              network === "whatsapp-demo-business-integration"
            ? "e.g. 972501234567@c.us"
            : network === "whatsapp-for-business"
            ? "e.g. 972501234567"
            : network === "email"
            ? "e.g. user@example.com"
            : network === "telegram"
            ? "e.g. 123456789"
            : network === "alice-and-bot"
            ? "e.g. UUID conversation ID"
            : "Conversation ID"}
          className={`w-full ${inputBaseClass}`}
          required
        />
        {network === "whatsapp" && (
          <p className="text-xs text-text-secondary-light dark:text-text-secondary">
            Include correct WhatsApp suffix (e.g., @c.us or @g.us)
          </p>
        )}
      </div>

      {network === "email" && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-text-primary-light dark:text-text-primary">
            Email Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) =>
              setSubject(
                (e.target as
                  | HTMLInputElement
                  | HTMLTextAreaElement
                  | HTMLSelectElement).value,
              )}
            placeholder="Subject line for the email thread"
            className={`w-full ${inputBaseClass}`}
            required
          />
        </div>
      )}

      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary-light dark:text-text-primary">
          Context
        </label>
        <textarea
          value={context}
          onChange={(e) =>
            setContext(
              (e.target as
                | HTMLInputElement
                | HTMLTextAreaElement
                | HTMLSelectElement).value,
            )}
          placeholder="Enter the context to inject into the conversation..."
          className={`w-full h-32 resize-y ${inputBaseClass}`}
          required
        />
      </div>

      {error && (
        <div className="p-3 text-sm text-error-light dark:text-error bg-error-light/10 dark:bg-error/10 border border-error-light/20 dark:border-error/20 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 text-sm text-success-light dark:text-success bg-success-light/10 dark:bg-success/10 border border-success-light/20 dark:border-success/20 rounded-lg">
          Context injected successfully!
        </div>
      )}

      <Button
        type="submit"
        disabled={loading || !network || !conversationId || !context ||
          (network === "email" && !subject)}
      >
        {loading ? "Injecting..." : "Inject Context"}
      </Button>
    </form>
  );
};
